import {
    ArticleClassification,
    ArticleContentTag,
    ArticleFields,
    ArticleMetadataFields,
    IArticle,
    IArticleImage,
    IArticleIssue,
    IArticlePublication,
    IArticlePublicationAuthor,
    IParagraph,
    IRelatedArticle,
} from '@pressreader/content-types';
import { buildIssueKey, getBestCoverImage, getBestImage, mapMissingMetadataToArticleField } from '@pressreader/content-utils';
import { error, warn } from '@pressreader/logger';
import { IScreenInfo } from '@pressreader/ui-types';
import { buildImageUrl, ensureTrailingSlash, hasFlag, parseDateString } from '@pressreader/utils';

import { ARTICLE_FALLBACK_LANGUAGE } from '../constants';
import { IArticleDto } from '../types';
import { ArticleClassificationDto } from '../types/article.classification';
import { IArticlePublicationDto } from '../types/article.publication';
import { IIssueDto } from '../types/issue';
import { AnyParagraphDto } from '../types/paragraphs/any.paragrpah';
import { IImageParagraphDto } from '../types/paragraphs/image.paragraph';
import { IRelatedArticleDto } from '../types/related.article';

import { mapImageUrl } from './map.image.url';
import { mapParagraph } from './paragraph.mapper';

interface ArticleImageServers {
    /** Base URL used to download an image by appending its identifier path. */
    imageDownload: string | null;
    /** Base URL of thumbs endpoint used with `regionKey` query parameter. */
    thumbs: string | null;
}

interface IArticleMapperContext {
    screenInfo: IScreenInfo;
    imageServers: ArticleImageServers;

    imagesSupported: boolean;
}

/**
 * Checks whether a paragraph DTO represents an image paragraph.
 * @param p - Paragraph DTO to inspect.
 * @returns `true` when the paragraph type is `image`; otherwise `false`.
 */
function isImageParagraph(p: AnyParagraphDto): p is IImageParagraphDto {
    return p.type === 'image';
}

/**
 * Extracts a top-level entity path prefix from image identifiers shaped as `<entity>/<uuid>`.
 * @param imageId - Raw image identifier from DTO.
 * @returns Entity prefix ending with `/`, or `null` when the identifier does not match the entity-image format.
 */
function getImageEntityPathPrefix(imageId: string): string | null {
    const imageEntityPath = /^([a-z][a-z0-9-]*\/)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const match = imageId.match(imageEntityPath);

    return match ? match[1] : null;
}

/**
 * Builds a canonical image-download URL by normalizing the server base URL and appending the image identifier.
 * @param imageDownloadBaseUrl - Image download server base URL.
 * @param imageId - Raw image identifier from DTO.
 * @returns Absolute image URL, or `null` when the base URL is not configured.
 */
function buildImageDownloadUrl(imageDownloadBaseUrl: string | null, imageId: string): string | null {
    if (!imageDownloadBaseUrl) {
        return null;
    }

    return buildImageUrl(ensureTrailingSlash(imageDownloadBaseUrl), imageId);
}

/**
 * Resolves the final image URL using mapper override, image-download server, or thumbs fallback.
 * @param params - Resolution inputs.
 * @param params.articleDto - Source article DTO used to check publication-specific URL strategy.
 * @param params.imageId - Image identifier to resolve.
 * @param params.imageDownloadBaseUrl - Base URL of the image-download service.
 * @param params.thumbsBaseUrl - Base URL of the thumbs endpoint used with `regionKey`.
 * @returns Resolved image URL string for rendering.
 */
function resolveImageUrl(params: {
    articleDto: IArticleDto;
    imageId: string;
    imageDownloadBaseUrl: string | null;
    thumbsBaseUrl: string | null;
}): string {
    const mappedImageUrl = mapImageUrl?.(params.imageId);

    if (mappedImageUrl !== undefined) {
        return mappedImageUrl;
    }

    const imageDownloadUrl = buildImageDownloadUrl(params.imageDownloadBaseUrl, params.imageId);
    const canUseImageDownload = !!imageDownloadUrl && (!!params.articleDto.publication || !!getImageEntityPathPrefix(params.imageId));
    if (canUseImageDownload && imageDownloadUrl) {
        return imageDownloadUrl;
    }

    if (params.thumbsBaseUrl) {
        return `${params.thumbsBaseUrl}?regionKey=${params.imageId}`;
    }

    if (imageDownloadUrl) {
        return imageDownloadUrl;
    }

    error(`Unable to resolve image URL for article image '${params.imageId}'.`);
    return params.imageId;
}

/**
 * Maps a single article image DTO into the domain image model.
 * @param params - Image mapping inputs.
 * @param params.articleDto - Source article DTO used for context-sensitive URL resolution.
 * @param params.image - Source image DTO item.
 * @param params.imageParagraphs - Image paragraph DTOs used for caption lookup.
 * @param params.imageDownloadBaseUrl - Base URL of the image-download service.
 * @param params.thumbsBaseUrl - Base URL of the thumbs endpoint used with `regionKey`.
 * @returns Mapped domain image.
 */
function mapImage(params: {
    articleDto: IArticleDto;
    image: NonNullable<IArticleDto['images']>[number];
    imageParagraphs: IImageParagraphDto[];
    imageDownloadBaseUrl: string | null;
    thumbsBaseUrl: string | null;
}): IArticleImage {
    const { image, imageParagraphs, articleDto, imageDownloadBaseUrl, thumbsBaseUrl } = params;
    const imgParagraph = imageParagraphs.find(imgP => imgP.imageId === image.id);

    return {
        id: image.id,
        url: resolveImageUrl({
            articleDto,
            imageId: image.id,
            imageDownloadBaseUrl,
            thumbsBaseUrl,
        }),
        size: image.size,
        caption: imgParagraph?.caption,
    };
}

/**
 * Maps article image DTOs into domain images with URL resolution and optional captions.
 * @param context - Mapper runtime context.
 * @param articleDto - Source article DTO.
 * @returns Mapped list of article images.
 */
function mapImages(context: IArticleMapperContext, articleDto: IArticleDto): IArticleImage[] {
    if (!context.imagesSupported) {
        return [];
    }

    const images = articleDto.images || [];
    const imageParagraphs = (articleDto.paragraphs || []).filter(isImageParagraph);
    const { imageDownload: imageDownloadBaseUrl, thumbs: thumbsBaseUrl } = context.imageServers;

    return images.map(image =>
        mapImage({
            articleDto,
            image,
            imageParagraphs,
            imageDownloadBaseUrl,
            thumbsBaseUrl,
        }),
    );
}

/**
 * Maps related-article images with thumbs-based URL strategy.
 * @param context - Mapper runtime context.
 * @param articleDto - Related article DTO.
 * @returns Mapped related-article images.
 */
function mapRelatedImages(context: IArticleMapperContext, articleDto: IRelatedArticleDto): IArticleImage[] {
    if (!context.imagesSupported) {
        return [];
    }

    const images = articleDto.images || [];
    const thumbsBaseUrl = context.imageServers.thumbs;

    return images.map<IArticleImage>(imageDto => {
        const imageBaseUrl = `${thumbsBaseUrl}?regionKey=`;

        return {
            id: imageDto.id,
            url: mapImageUrl?.(imageDto.id) ?? imageBaseUrl + imageDto.id,
            size: imageDto.size,
        };
    });
}

/**
 * Maps issue DTO to domain issue, resolving issue key from DTO or reconstructed values.
 * @param issueDto - Source issue DTO.
 * @returns Mapped issue, or `null` when issue data is missing/invalid.
 */
function mapIssue(issueDto: IIssueDto): IArticleIssue | null {
    if (!issueDto) {
        return null;
    }

    const issueDate = parseDateString(issueDto.date);

    if (!issueDate) {
        return null;
    }

    let issueKey: string;
    try {
        issueKey = buildIssueKey(issueDto.newspaper.cid, issueDate, issueDto.issueVer);
    } catch (err) {
        warn(`Unable to resolve issue key for article issue id: ${issueDto.id}.`, err);
        return null;
    }

    return {
        issueKey,
        id: issueDto.id,
        date: issueDate,
        issueVer: issueDto.issueVer,
        issueVersion: issueDto.issueVersion,
        expungeVersion: issueDto.expungeVersion,
        firstPageSize: issueDto.firstPageSize,
        newspaper: issueDto.newspaper,
        page: issueDto.page,
        slugs: issueDto.slugs,
        sectionName: issueDto.sectionName,
        primaryArticle: issueDto.primaryArticle ? { articleId: issueDto.primaryArticle.articleId, page: issueDto.primaryArticle.page } : null,
    };
}

/**
 * Maps classification DTO into domain classification model.
 * @param dto - Source classification DTO.
 * @returns Domain classification model.
 */
function mapArticleClassification(dto: ArticleClassificationDto | null | undefined): ArticleClassification {
    if (!dto) {
        return { tags: [] };
    }
    return {
        clusterId: dto.clusterId,
        summary: dto.summary,
        tags: dto.tags.map<ArticleContentTag>(t => ({
            id: t.id,
            displayName: t.displayName,
            slug: t.slug,
            weight: t.weight,
            sentiment: t.sentiment === 'NEGATIVE' ? -1 : t.sentiment === 'POSITIVE' ? 1 : 0,
        })),
        sentiment: dto.sentiment,
    };
}

/**
 * Maps related article DTO to related article domain model.
 * @param context - Mapper runtime context.
 * @param relatedDto - Source related article DTO.
 * @returns Mapped related article.
 */
function mapRelated(context: IArticleMapperContext, relatedDto: IRelatedArticleDto): IRelatedArticle {
    return {
        id: `${relatedDto.id}`,
        issue: mapIssue(relatedDto.issue),
        shortContent: relatedDto.shortContent,
        title: relatedDto.title,
        classification: mapArticleClassification(relatedDto.classification),
        image: getBestImage(mapRelatedImages(context, relatedDto)),
    };
}

/**
 * Maps publication DTO into domain publication model.
 * @param publicationDto - Source publication DTO.
 * @returns Mapped publication, or `null` when required publication data is missing.
 */
function mapPublication(publicationDto: IArticlePublicationDto): IArticlePublication | null {
    if (!publicationDto || !publicationDto.author) {
        return null;
    }

    const date = new Date(publicationDto.date);
    const author: IArticlePublicationAuthor = {
        profileId: publicationDto.author.profileId,
        name: publicationDto.author.name,
        imageId: publicationDto.author.imageId,
    };

    return {
        date,
        author,
    };
}

function getAvailableFields(articleDto: IArticleDto, issue: IArticleIssue | null, publication: IArticlePublication | null): ArticleFields {
    let availableFields = ArticleFields.None;
    if (articleDto.shortContent) {
        availableFields |= ArticleFields.ShortContent;
    }

    if (articleDto.images) {
        availableFields |= ArticleFields.Images;
    }

    if (issue) {
        availableFields |= ArticleFields.Issue;
    }

    if (publication) {
        availableFields |= ArticleFields.Publication;
    }

    if (articleDto.paragraphs) {
        availableFields |= ArticleFields.Paragraphs;
    }

    if (articleDto.related) {
        availableFields |= ArticleFields.Related;
    }

    if (articleDto.similars) {
        availableFields |= ArticleFields.Similars;
    }

    if (articleDto.access) {
        availableFields |= ArticleFields.Access;
    }

    if (articleDto.comments !== undefined) {
        availableFields |= ArticleFields.Comments;
    }

    if (articleDto.votes) {
        availableFields |= ArticleFields.Votes;
    }

    if (articleDto.analyticProfiles) {
        availableFields |= ArticleFields.AnalyticProfiles;
    }

    if (articleDto.classification) {
        availableFields |= ArticleFields.Classification;
    }

    if (articleDto.classification?.summary != null) {
        availableFields |= ArticleFields.Summary;
    }

    return availableFields;
}

/**
 * Calculates required fields that are missing from the available field set.
 * @param availableFields - Bitmask of available fields.
 * @param requiredFields - Bitmask of requested fields.
 * @returns Bitmask of required-but-missing fields.
 */
function getRestrictedFields(availableFields: ArticleFields, requiredFields: ArticleFields): ArticleFields {
    const fieldsToCheck = [
        ArticleFields.ShortContent,
        ArticleFields.Images,
        ArticleFields.Issue,
        ArticleFields.Publication,
        ArticleFields.Paragraphs,
        ArticleFields.Related,
        ArticleFields.Similars,
        ArticleFields.Access,
        ArticleFields.Comments,
        ArticleFields.Votes,
        ArticleFields.AnalyticProfiles,
        ArticleFields.Classification,
        // Summary must be accounted like every other requested field: leaving it out of both
        // available- and restricted-tracking made every `all`-fields load re-fetch the whole
        // article, because a requested-but-absent summary could never be marked as resolved.
        ArticleFields.Summary,
    ];

    let restrictedFields = ArticleFields.None;
    fieldsToCheck.forEach(field => {
        if (hasFlag(requiredFields, field) && !hasFlag(availableFields, field)) {
            restrictedFields |= field;
        }
    });

    return restrictedFields;
}

function getMissingMetadata(params: {
    articleDto: IArticleDto;
    requiredFields: ArticleFields;
    availableFields: ArticleFields;
    language: string;
    originalLanguage: string;
}): ArticleMetadataFields | undefined {
    const { articleDto, requiredFields, availableFields, language, originalLanguage } = params;

    let missingMetadata: ArticleMetadataFields | undefined;
    if (!articleDto.language) {
        warn(`Article ${articleDto.id} does not have a language. Using fallback language '${language}'.`);
        missingMetadata = ArticleMetadataFields.Language;
    }
    if (!articleDto.originalLanguage) {
        warn(`Article ${articleDto.id} does not have an original language. Using fallback language '${originalLanguage}'.`);
        missingMetadata = missingMetadata ? missingMetadata | ArticleMetadataFields.OriginalLanguage : ArticleMetadataFields.OriginalLanguage;
    }

    const issueWasRequestedAndMissing = hasFlag(requiredFields, ArticleFields.Issue) && !hasFlag(availableFields, ArticleFields.Issue);
    if (issueWasRequestedAndMissing) {
        missingMetadata = missingMetadata ? missingMetadata | ArticleMetadataFields.Issue : ArticleMetadataFields.Issue;
    }

    return missingMetadata;
}

function mapArticle(context: IArticleMapperContext, articleDto: IArticleDto, requiredFields: ArticleFields = ArticleFields.None): IArticle {
    const { imagesSupported } = context;

    const issue = mapIssue(articleDto.issue);
    const publication = mapPublication(articleDto.publication);
    const availableFields = getAvailableFields(articleDto, issue, publication);
    const language = articleDto.language ?? articleDto.originalLanguage ?? ARTICLE_FALLBACK_LANGUAGE;
    const originalLanguage = articleDto.originalLanguage ?? language;
    const missingMetadata = getMissingMetadata({
        articleDto,
        requiredFields,
        availableFields,
        language,
        originalLanguage,
    });

    const missingFields = mapMissingMetadataToArticleField(missingMetadata);
    let restrictedFields = getRestrictedFields(availableFields, requiredFields);
    if (missingFields) {
        restrictedFields &= ~missingFields;
    }

    const images = mapImages(context, articleDto);

    const isPreview = !articleDto.paragraphs;

    return {
        id: `${articleDto.id}`,
        primaryArticleId: articleDto.issue?.primaryArticle ? articleDto.issue?.primaryArticle.articleId.toString() : `${articleDto.id}`,
        access: articleDto.access,
        author: articleDto.author,
        date: new Date(articleDto.date),
        coverImage: imagesSupported ? getBestCoverImage(images, context.screenInfo) : null,
        bestImage: imagesSupported ? getBestImage(images) : null,
        images: images,
        issue,
        publication,
        language,
        originalLanguage,
        rank: articleDto.rank,
        rate: articleDto.rate,
        related: (articleDto.related || []).map(relatedDto => mapRelated(context, relatedDto)),
        similars: articleDto.similars ? { count: articleDto.similars.count } : null,
        shortContent: articleDto.shortContent,
        subtitle: articleDto.subtitle,
        title: articleDto.title,

        availableTextLength: isPreview ? (articleDto.shortContent || '').length : articleDto.textLength,
        textLength: articleDto.textLength,
        readingTimeMinutes: articleDto.readingTimeMinutes,

        paragraphs: (articleDto.paragraphs || []).map(p => mapParagraph(context, p, articleDto, images)).filter(p => p) as IParagraph[],

        comments: articleDto.comments,
        votes: articleDto.votes,

        analyticProfiles: articleDto.analyticProfiles,
        classification: mapArticleClassification(articleDto.classification),

        availableFields,
        restrictedFields,

        articleSource: articleDto.articleSource,

        missingMetadata,
    };
}

/**
 * Converts a related article model into a lightweight article model.
 * @param relatedArticle - Source related article model.
 * @returns Lightweight article model built from related-article data.
 */
function mapRelatedToArticle(relatedArticle: IRelatedArticle): IArticle {
    const article = {
        id: relatedArticle.id,
        primaryArticleId: relatedArticle.id,
        issue: { ...relatedArticle.issue },
        title: relatedArticle.title,
        shortContent: relatedArticle.shortContent,
    } as IArticle;

    if (article.shortContent) {
        article.availableFields |= ArticleFields.ShortContent;
    }

    if (article.issue) {
        article.availableFields |= ArticleFields.Issue;
    }

    return article;
}

export { IArticleMapperContext, mapArticle, mapIssue, mapRelatedToArticle };
